// Raw transcript renderer with word-level audio sync.
//
// RAW detail items expose `data.text` as a JSON-encoded array of word
// records. Example word record:
//
//   {
//     "text": "uh",
//     "start_ms": 1779208738654,        // epoch ms — normalize via audioStartEpochMs
//     "end_ms":   1779208738733,
//     "speaker_id": "S2546_00",
//     "source_snippet_id": 2546,
//     ...
//   }
//
// Visual layout:
//   - Group words by `source_snippet_id` (defines a snippet card).
//   - Within each snippet, walk words in chronological order; open a new
//     turn block whenever `speaker_id` changes.
//   - One <span> per word, with the same highlight + click-to-seek
//     contract as TaggedTranscriptView.
//
// The component is purposely stateless beyond a scroll-pause timer;
// `currentTimeMs` flows in from the parent (hoisted player state).
//
// Snippet boundaries are also exposed via the named export
// `collectSnippetBoundaries(items, audioStartEpochMs)` so the audio
// player can paint WaveSurfer regions over the same boundaries.

import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
} from "@chakra-ui/react";

const SPEAKER_COLORS = ["blue", "purple", "green", "orange", "teal", "pink"];

const SCROLL_PAUSE_AFTER_USER_INTERACTION_MS = 5000;

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

// Pull the word-record array from a single RAW detail item. `data.text` is
// the canonical location (JSON-encoded string); tolerate `data.words` /
// `data` as fallbacks so legacy shapes still render.
function parseWordsFromItem(item) {
  const data = item?.data || {};
  if (typeof data.text === "string") {
    const trimmed = data.text.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (_e) {
        return [];
      }
    }
    return [];
  }
  if (Array.isArray(data.words)) return data.words;
  if (Array.isArray(data)) return data;
  return [];
}

// Build the flat list of words across every RAW item, in chronological
// `start_ms` order. Each word carries:
//   { text, start_ms, end_ms, speaker_id, source_snippet_id }
// The values pass through unchanged — epoch normalization happens at
// render time so the same array can drive both word highlight + region
// boundaries (which the audio player also normalizes against epoch).
function collectAllWords(items) {
  const collected = [];
  if (!Array.isArray(items)) return collected;
  const sorted = [...items].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
  for (const item of sorted) {
    const words = parseWordsFromItem(item);
    for (const w of words) collected.push(w);
  }
  collected.sort(
    (a, b) => (Number(a?.start_ms) || 0) - (Number(b?.start_ms) || 0)
  );
  return collected;
}

// Group words into per-snippet buckets, preserving chronological order.
// Returns `[{ snippet_id, words: [...], start_ms, end_ms }, ...]`.
function groupBySnippet(words) {
  const buckets = [];
  let current = null;
  for (const w of words) {
    const sid = w?.source_snippet_id ?? null;
    if (current === null || current.snippet_id !== sid) {
      current = {
        snippet_id: sid,
        words: [],
        start_ms: Number(w?.start_ms) || 0,
        end_ms: Number(w?.end_ms) || 0,
      };
      buckets.push(current);
    }
    current.words.push(w);
    const end = Number(w?.end_ms);
    if (Number.isFinite(end) && end > current.end_ms) current.end_ms = end;
  }
  return buckets;
}

// Inside one snippet, open a new turn whenever speaker_id changes.
// Returns `[{ speaker_id, words: [...] }, ...]`.
function groupByTurn(words) {
  const turns = [];
  let current = null;
  for (const w of words) {
    const sp = w?.speaker_id ?? null;
    if (current === null || current.speaker_id !== sp) {
      current = { speaker_id: sp, words: [] };
      turns.push(current);
    }
    current.words.push(w);
  }
  return turns;
}

// Stable speaker color allocator — passed as a fresh `Map` per render so
// colors stay stable within a single render but don't bleed across
// re-mounts.
function colorForSpeaker(key, registry) {
  if (!key) return "gray";
  if (registry.has(key)) return registry.get(key);
  const idx = registry.size % SPEAKER_COLORS.length;
  const color = SPEAKER_COLORS[idx];
  registry.set(key, color);
  return color;
}

// Friendly speaker label — "S2546_00" → "Speaker 0", anything else
// returns the raw id.
function speakerLabel(rawSpeaker) {
  if (!rawSpeaker) return "Speaker";
  const s = String(rawSpeaker).trim();
  // Common shape from the tagger: "S{snippet_id}_{NN}".
  const m = s.match(/^S\d+_0*(\d+)$/i);
  if (m) return `Speaker ${m[1]}`;
  return s;
}

// ---------------------------------------------------------------------------
// Snippet boundary helper — exposed for the audio player.
// ---------------------------------------------------------------------------

// Return per-snippet boundary objects in conv-relative SECONDS, ready
// for direct hand-off to a WaveSurfer regions plugin:
//   [{ snippet_id, start: 12.3, end: 18.7 }, ...]
//
// `audioStartEpochMs` is subtracted from epoch start_ms / end_ms before
// converting to seconds. Boundaries with non-finite or negative results
// are dropped.
export function collectSnippetBoundaries(items, audioStartEpochMs = 0) {
  const words = collectAllWords(items);
  const buckets = groupBySnippet(words);
  const offset = Number(audioStartEpochMs) || 0;
  const out = [];
  for (const b of buckets) {
    const startMs = (Number(b.start_ms) || 0) - offset;
    const endMs = (Number(b.end_ms) || 0) - offset;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    if (endMs <= startMs) continue;
    out.push({
      snippet_id: b.snippet_id,
      start: Math.max(0, startMs / 1000),
      end: Math.max(0, endMs / 1000),
    });
  }
  return out;
}

// Find the active word across the flat (snippet → turn → word) tree.
// Returns `{ snippetIdx, turnIdx, wordIdx }` or `null` when
// `currentTimeMs` precedes the first word.
//
// STICKY BRACKETING: "the last word whose start_ms <= currentTimeMs."
// Strict inclusive bracketing on `[start_ms, end_ms]` would flicker off
// in the gaps between words and during the long stretches between
// WaveSurfer's ~4Hz `timeupdate` ticks. With sticky bracketing every
// tick that lands inside the conversation maps to exactly one word.
function findActiveWord(snippets, currentTimeMs, epochOffset = 0) {
  if (!Array.isArray(snippets) || !Number.isFinite(currentTimeMs)) return null;
  const off = Number(epochOffset) || 0;
  let best = null;
  for (let si = 0; si < snippets.length; si += 1) {
    const snip = snippets[si];
    if (!snip || !Array.isArray(snip.turns)) continue;
    for (let ti = 0; ti < snip.turns.length; ti += 1) {
      const turn = snip.turns[ti];
      const words = Array.isArray(turn?.words) ? turn.words : [];
      for (let wi = 0; wi < words.length; wi += 1) {
        const start = Number(words[wi]?.start_ms);
        if (!Number.isFinite(start)) continue;
        if (currentTimeMs < start - off) {
          // Words walk forward in time within a turn — first miss
          // means this turn's later words also haven't started.
          return best;
        }
        best = { snippetIdx: si, turnIdx: ti, wordIdx: wi };
      }
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

const RawTurn = ({
  turn,
  turnIdx,
  snippetIdx,
  activeWordIdx,
  onSeekToMs,
  epochOffset,
  speakerRegistry,
}) => {
  const speakerKey = turn.speaker_id ?? `unknown_${snippetIdx}_${turnIdx}`;
  const label = speakerLabel(turn.speaker_id);
  const color = colorForSpeaker(speakerKey, speakerRegistry);
  // First word's start drives the turn timestamp display.
  const firstWord = turn.words[0];
  const turnStartMs = Math.max(
    0,
    (Number(firstWord?.start_ms) || 0) - epochOffset
  );

  return (
    <Flex
      gap={3}
      align="flex-start"
      data-testid="raw-turn"
      data-turn-idx={turnIdx}
      data-snippet-idx={snippetIdx}
      data-speaker={label}
    >
      <Avatar
        name={label}
        size="sm"
        bg={`${color}.500`}
        color="white"
        flexShrink={0}
      />
      <Box flex={1} minW={0}>
        <Flex align="baseline" gap={2} mb={1}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={`${color}.600`}
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {label}
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            fontFamily="mono"
            data-testid="raw-turn-timestamp"
          >
            {formatTimestamp(turnStartMs)}
          </Text>
        </Flex>
        <Box
          bg="gray.50"
          borderRadius="lg"
          borderLeftWidth="3px"
          borderLeftColor={`${color}.300`}
          px={4}
          py={2}
        >
          <Text fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap">
            {turn.words.map((w, wi) => {
              const isActive = wi === activeWordIdx;
              const wordStart = Math.max(
                0,
                (Number(w?.start_ms) || 0) - epochOffset
              );
              const wordEnd = Math.max(
                0,
                (Number(w?.end_ms) || 0) - epochOffset
              );
              const text = w?.text ?? w?.word ?? "";
              return (
                <React.Fragment key={wi}>
                  <Box
                    as="span"
                    cursor="pointer"
                    bg={isActive ? "yellow.200" : "transparent"}
                    borderRadius="sm"
                    px={isActive ? 1 : 0}
                    transition="background-color 100ms ease"
                    data-testid="raw-word"
                    data-word-idx={wi}
                    data-turn-idx={turnIdx}
                    data-snippet-idx={snippetIdx}
                    data-start-ms={wordStart}
                    data-end-ms={wordEnd}
                    data-active={isActive ? "true" : "false"}
                    onClick={() => onSeekToMs?.(wordStart)}
                  >
                    {text}
                  </Box>
                  {wi < turn.words.length - 1 ? " " : null}
                </React.Fragment>
              );
            })}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
};

function formatTimestamp(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SnippetCard = ({
  snippet,
  snippetIdx,
  activeTurnIdx,
  activeWordIdx,
  onSeekToMs,
  epochOffset,
  snippetRef,
  speakerRegistry,
}) => {
  const startMs = Math.max(0, (Number(snippet.start_ms) || 0) - epochOffset);
  return (
    <Box
      ref={snippetRef}
      borderWidth="1px"
      borderColor="gray.200"
      borderStyle="solid"
      borderRadius="lg"
      p={3}
      data-testid="raw-snippet"
      data-snippet-id={snippet.snippet_id ?? ""}
      data-snippet-idx={snippetIdx}
    >
      <HStack mb={2} spacing={2}>
        <Badge
          variant="subtle"
          colorScheme="gray"
          fontSize="xx-small"
          data-testid="raw-snippet-badge"
        >
          Snippet S{snippet.snippet_id ?? "?"}
        </Badge>
        <Text fontSize="xs" color="gray.400" fontFamily="mono">
          {formatTimestamp(startMs)}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {snippet.turns.map((turn, ti) => (
          <RawTurn
            key={ti}
            turn={turn}
            turnIdx={ti}
            snippetIdx={snippetIdx}
            activeWordIdx={ti === activeTurnIdx ? activeWordIdx : -1}
            onSeekToMs={onSeekToMs}
            epochOffset={epochOffset}
            speakerRegistry={speakerRegistry}
          />
        ))}
      </VStack>
    </Box>
  );
};

const RawTranscriptView = ({
  items,
  currentTimeMs = 0,
  onSeekToMs,
  audioStartEpochMs = 0,
  legacyRenderer,
}) => {
  const scrollPausedUntilRef = useRef(0);
  const lastScrolledSnippetRef = useRef(-1);
  const snippetRefs = useRef([]);

  // Walk RAW items → flat words → snippet buckets → per-snippet turns.
  const snippets = useMemo(() => {
    const words = collectAllWords(items);
    const buckets = groupBySnippet(words);
    return buckets.map((b) => ({
      snippet_id: b.snippet_id,
      start_ms: b.start_ms,
      end_ms: b.end_ms,
      turns: groupByTurn(b.words),
    }));
  }, [items]);

  // A fresh registry per render keeps speaker colors stable within
  // one render pass while allowing the palette to reset between mounts.
  const speakerRegistry = useMemo(() => new Map(), [snippets]);

  const active = useMemo(
    () => findActiveWord(snippets, currentTimeMs, audioStartEpochMs),
    [snippets, currentTimeMs, audioStartEpochMs]
  );

  // Pause auto-scroll for a few seconds after user-initiated scrolling.
  useEffect(() => {
    const onWheel = () => {
      scrollPausedUntilRef.current =
        Date.now() + SCROLL_PAUSE_AFTER_USER_INTERACTION_MS;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchmove", onWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onWheel);
    };
  }, []);

  // Scroll the active snippet into view as the active word advances.
  useEffect(() => {
    if (!active) return;
    if (active.snippetIdx === lastScrolledSnippetRef.current) return;
    if (Date.now() < scrollPausedUntilRef.current) return;
    const el = snippetRefs.current[active.snippetIdx];
    if (el && typeof el.scrollIntoView === "function") {
      try {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch {
        /* jsdom */
      }
    }
    lastScrolledSnippetRef.current = active.snippetIdx;
  }, [active]);

  // Empty state — no parseable RAW items. Fall through to legacy renderer
  // (preserves the existing fallback behavior for old/odd-shaped data).
  if (!snippets.length) {
    if (typeof legacyRenderer === "function") {
      const sorted = Array.isArray(items)
        ? [...items].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
        : [];
      return legacyRenderer(sorted);
    }
    return null;
  }

  return (
    <VStack
      align="stretch"
      spacing={4}
      data-testid="raw-transcript-structured"
    >
      {snippets.map((snippet, si) => (
        <SnippetCard
          key={si}
          snippet={snippet}
          snippetIdx={si}
          activeTurnIdx={active?.snippetIdx === si ? active.turnIdx : -1}
          activeWordIdx={active?.snippetIdx === si ? active.wordIdx : -1}
          onSeekToMs={onSeekToMs}
          epochOffset={audioStartEpochMs}
          speakerRegistry={speakerRegistry}
          snippetRef={(el) => {
            snippetRefs.current[si] = el;
          }}
        />
      ))}
    </VStack>
  );
};

export default RawTranscriptView;
export { findActiveWord, collectAllWords, groupBySnippet, groupByTurn };
